import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';

export class TitleScreen implements Screen {
  readonly state = ScreenState.TITLESCREEN;
  private context: ScreenContext;
  private showNameInput: boolean = false;
  private teamNameInput: string = '';

  constructor(context: ScreenContext) {
    this.context = context;
  }

  enter(): void {
    console.log('Entering TitleScreen');
    this.showNameInput = false;
    this.teamNameInput = '';
  }

  exit(): void {
    console.log('Exiting TitleScreen');
    this.context.input?.blurMobileInput();
  }

  update(_deltaTime: number): void {}

  render(canvas: Canvas): void {
    // Draw background
    canvas.drawImage(this.context.assets.titleScreen, 0, 0);

    // Draw game title with outline
    const titleX = CONSTANTS.CANVAS_WIDTH / 2;
    const titleY = 180;
    canvas.drawOutlinedText(
      CONSTANTS.GAME_NAME,
      titleX,
      titleY,
      CONSTANTS.GAME_NAME_FONT,
      '#ffffff',
      CONSTANTS.GAME_NAME_OUTLINE,
      3,
      'center'
    );

    // Draw version
    canvas.drawText(
      CONSTANTS.GAME_VERSION,
      titleX,
      titleY + 60,
      CONSTANTS.GAME_VERSION_FONT,
      '#ffffff',
      'center'
    );

    if (this.showNameInput) {
      this.renderNameInput(canvas);
    } else {
      this.renderButtons(canvas);
    }
  }

  private renderButtons(canvas: Canvas): void {
    const buttonWidth = 150;
    const buttonHeight = 30;
    const buttonX = (CONSTANTS.CANVAS_WIDTH - buttonWidth) / 2;
    const buttonStartY = 320;
    const buttonSpacing = 40;

    // New game button
    canvas.drawButton(
      'new-game',
      CONSTANTS.NEW_GAME_TXT,
      buttonX,
      buttonStartY,
      buttonWidth,
      buttonHeight,
      CONSTANTS.TITLESCREEN_BUTTON_FONT,
      '#ffffff',
      undefined,
      () => {
        this.showNameInput = true;
        this.teamNameInput = '';
      }
    );

    // Load game button - only show if save exists
    const hasSave = this.context.hasSaveGame?.() ?? false;
    if (hasSave) {
      canvas.drawButton(
        'load-game',
        CONSTANTS.LOAD_GAME_TXT,
        buttonX,
        buttonStartY + buttonSpacing,
        buttonWidth,
        buttonHeight,
        CONSTANTS.TITLESCREEN_BUTTON_FONT,
        '#ffffff',
        '#006600',
        () => {
          if (this.context.onLoadGame) {
            this.context.onLoadGame();
          }
        }
      );
    } else {
      canvas.drawRect(buttonX, buttonStartY + buttonSpacing, buttonWidth, buttonHeight, '#444444');
      canvas.drawText(
        CONSTANTS.LOAD_GAME_TXT,
        buttonX + buttonWidth / 2,
        buttonStartY + buttonSpacing + 8,
        CONSTANTS.TITLESCREEN_BUTTON_FONT,
        '#888888',
        'center'
      );
    }

    // Exit button
    canvas.drawButton(
      'exit',
      CONSTANTS.EXIT_GAME_TXT,
      buttonX,
      buttonStartY + buttonSpacing * 2,
      buttonWidth,
      buttonHeight,
      CONSTANTS.TITLESCREEN_BUTTON_FONT,
      '#ffffff',
      undefined,
      () => {
        console.log('Exit clicked - not applicable in browser');
      }
    );
  }

  private renderNameInput(canvas: Canvas): void {
    const dialogW = 350;
    const dialogH = 150;
    const dialogX = (CONSTANTS.CANVAS_WIDTH - dialogW) / 2;
    const dialogY = 300;

    // Dialog background
    canvas.drawRect(dialogX, dialogY, dialogW, dialogH, '#222222');
    canvas.drawRectOutline(dialogX, dialogY, dialogW, dialogH, '#888888');

    // Label
    canvas.drawText('Joukkueen nimi:', dialogX + dialogW / 2, dialogY + 15, CONSTANTS.BIG_FONT, '#ffffff', 'center');

    // Input field
    const inputW = 280;
    const inputH = 32;
    const inputX = dialogX + (dialogW - inputW) / 2;
    const inputY = dialogY + 50;

    canvas.drawRect(inputX, inputY, inputW, inputH, '#ffffff');
    canvas.drawRectOutline(inputX, inputY, inputW, inputH, '#0066ff');

    const displayText = this.teamNameInput + '|';
    canvas.drawText(displayText, inputX + 10, inputY + 8, CONSTANTS.BIG_FONT, '#000000');

    // Clickable region to refocus mobile input when tapping the input field
    canvas.addClickableRegion({
      id: 'team-name-input',
      x: inputX,
      y: inputY,
      width: inputW,
      height: inputH,
      onClick: () => {
        this.context.input?.focusMobileInput(this.teamNameInput, 'text');
      },
    });

    // Buttons
    const canStart = this.teamNameInput.trim().length > 0;
    const btnY = dialogY + 100;

    if (canStart) {
      canvas.drawButton('start-game', 'Aloita', dialogX + dialogW / 2 - 110, btnY, 100, 30,
        CONSTANTS.MAIN_BUTTON_FONT, '#ffffff', '#006600', () => {
          this.context.onStartNewGame?.(this.teamNameInput.trim());
        });
    } else {
      canvas.drawRect(dialogX + dialogW / 2 - 110, btnY, 100, 30, '#444444');
      canvas.drawText('Aloita', dialogX + dialogW / 2 - 60, btnY + 8, CONSTANTS.MAIN_BUTTON_FONT, '#888888', 'center');
    }

    canvas.drawButton('cancel', 'Peruuta', dialogX + dialogW / 2 + 10, btnY, 100, 30,
      CONSTANTS.MAIN_BUTTON_FONT, '#ffffff', '#660000', () => {
        this.showNameInput = false;
      });
  }

  handleClick(x: number, y: number): void {
    console.log(`TitleScreen click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (this.showNameInput) {
      // Handle mobile input events
      if (key.startsWith('__mobileInput:')) {
        const value = key.slice('__mobileInput:'.length);
        this.teamNameInput = value.slice(0, 24);
        return;
      }

      if (key === 'Escape') {
        this.showNameInput = false;
        this.context.input?.blurMobileInput();
      } else if (key === 'Enter') {
        if (this.teamNameInput.trim().length > 0) {
          this.context.input?.blurMobileInput();
          this.context.onStartNewGame?.(this.teamNameInput.trim());
        }
      } else if (key === 'Backspace') {
        this.teamNameInput = this.teamNameInput.slice(0, -1);
      } else if (key.length === 1 && this.teamNameInput.length < 24) {
        this.teamNameInput += key;
      }
      return;
    }

    if (key === 'Escape') {
      console.log('ESC pressed on TitleScreen');
    }
  }
}
